import React, { Suspense, useEffect, useMemo } from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom'

import { YStack } from 'tamagui'

import { getSlugFromPrettyUrl } from '@data/util/urlHelpers'

import { EventMetadata } from '@newComponents/Event/EventMetadata'
import { Card } from '@newComponents/Card'
import { GradientBackgroundScreen } from '@newComponents/Screen'
import { Spinner } from '@newComponents/Spinner'
import { H2, Text } from '@newComponents/Text'

import { EventItem } from '@newComponents/Event/EventItem'
import { lazy as loadable } from '@loadable/component'

import { EventAttendContent } from './EventAttendContent'

import { EventAttendScreenQuery } from './__generated__/EventAttendScreenQuery.graphql'

const EventAttendFormDialog = loadable(
  () => import('../../modals/EventAttendForm/EventAttendFormDialog')
)

export const EventAttendScreen = () => {
  const params = useParams<'eventId'>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const data = useLazyLoadQuery<EventAttendScreenQuery>(
    graphql`
      query EventAttendScreenQuery($slug: String!) {
        event: eventBySlug(slug: $slug) {
          name
          prettyUrl
          eventTicketingTiers {
            totalCount
          }

          owner {
            __typename

            ... on User {
              firstName
              lastName
            }

            ... on Guild {
              name
              slugId

              myMembership {
                membershipLevel
                status
              }
            }
          }

          myAttendance {
            ticketPurchase {
              status
            }
          }

          attendeeForm {
            rowId
          }

          ...EventItemNew_event
          ...EventAttendContent_event
          ...EventMetadata_event
        }

        ...EventItemNew_query
        ...EventAttendContent_query
      }
    `,
    { slug: getSlugFromPrettyUrl(params.eventId) }
  )

  const name = useMemo(() => {
    if (!data.event) {
      return ''
    }

    switch (data.event.owner?.__typename) {
      case 'Guild': {
        return data.event.owner.name
      }

      case 'User': {
        return `${data.event.owner.firstName} ${data.event.owner.lastName}`
      }
    }

    return ''
  }, [data])

  // Redirect non-members to guild confirm before allowing event attendance.
  // This is a safety net: the attend button should already route through guild
  // confirm, but this catches direct URL access and page refreshes.
  const needsGuildMembership =
    data.event?.owner?.__typename === 'Guild' &&
    !data.event.owner.myMembership

  useEffect(() => {
    if (!needsGuildMembership || !data.event) return

    const owner = data.event.owner
    if (owner?.__typename !== 'Guild') return

    const status = searchParams.get('status') || 'ATTENDING_IN_PERSON'
    const eventAttendURL = `/events/${data.event.prettyUrl}/attend?status=${status}`

    navigate(
      `/${owner.slugId}/confirm?eventAttendPath=${encodeURIComponent(eventAttendURL)}`,
      {
        replace: true,
        state: {
          backgroundLocation: (
            location.state as { backgroundLocation?: Location } | undefined
          )?.backgroundLocation,
        },
      }
    )
  }, [needsGuildMembership, data.event, navigate, location.state, searchParams])

  if (!data.event || needsGuildMembership) {
    return null
  }

  const pageTitle =
    data.event.eventTicketingTiers.totalCount > 0 &&
    data.event.myAttendance?.ticketPurchase?.status !== 'SUCCEEDED'
      ? `Purchase Ticket for ${data.event.name}`
      : `Confirm Attendance for ${data.event.name}`

  return (
    <>
      <EventMetadata title={pageTitle} event={data.event} />

      <GradientBackgroundScreen alignItems='center' gap='$8'>
        <EventItem
          event={data.event}
          query={data}
          maxWidth={600}
          width='100%'
          showAttendButton={false}
          displayFullDate
          displayVisibility={false}
        />

        <Card gap='$6' maxWidth={600} width='100%'>
          {data.event.attendeeForm ? (
            <Suspense
              fallback={
                <YStack
                  flex={1}
                  alignItems='center'
                  justifyContent='center'
                >
                  <Spinner size='$9' color='$primaryColor' />
                </YStack>
              }
            >
              <EventAttendFormDialog />
            </Suspense>
          ) : (
            <>
              <YStack gap='$1'>
                <H2>Almost there...</H2>

                <Text size='$4'>
                  {data.event.eventTicketingTiers.totalCount > 0 &&
                  data.event.myAttendance?.ticketPurchase?.status !==
                    'SUCCEEDED'
                    ? `Do you want to purchase a ticket for ${data.event.name}?`
                    : data.event.owner?.__typename === 'Guild' &&
                      data.event.owner.myMembership?.membershipLevel
                    ? `We're confirming your attendance for ${data.event.name}`
                    : `We need your consent to share information with ${name}`}
                </Text>
              </YStack>

              <EventAttendContent event={data.event} query={data} />
            </>
          )}
        </Card>
      </GradientBackgroundScreen>
    </>
  )
}

export default EventAttendScreen
